import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API_BASE_URL as API } from '../config/api';
import {
  Bell, UserPlus, Heart, MessageCircle, AtSign, Repeat,
  AlertTriangle, Megaphone, Star, Trophy, Check, CheckCheck,
  Trash2, Loader2, Settings, ArrowLeft, Filter, DollarSign,
  Music, Radio, Swords, Users, Eye
} from 'lucide-react';
import { cn } from '../lib/utils';

const TAB_CONFIG = {
  all: { label: 'All', icon: Bell },
  mentions: { label: 'Mentions', icon: AtSign },
  activity: { label: 'Activity', icon: Heart },
  earnings: { label: 'Earnings', icon: DollarSign },
};

const ICON_MAP = {
  follow: UserPlus,
  like_post: Heart,
  like_track: Heart,
  comment: MessageCircle,
  mention: AtSign,
  reply: MessageCircle,
  repost: Repeat,
  system: Bell,
  announcement: Megaphone,
  strike_warning: AlertTriangle,
  featured: Star,
  milestone: Trophy,
  tip_received: DollarSign,
  earnings: DollarSign,
  battle_invite: Swords,
  battle_result: Trophy,
  went_live: Radio,
  new_track: Music,
  "user-plus": UserPlus,
  heart: Heart,
  "message-circle": MessageCircle,
  "dollar-sign": DollarSign,
};

const ICON_COLORS = {
  follow: 'bg-[#A78BFA]/15 text-[#A78BFA]',
  like_post: 'bg-[#FF3B30]/15 text-[#FF3B30]',
  like_track: 'bg-[#FF3B30]/15 text-[#FF3B30]',
  comment: 'bg-[#007AFF]/15 text-[#007AFF]',
  mention: 'bg-[#A78BFA]/15 text-[#A78BFA]',
  tip_received: 'bg-[#34D399]/15 text-[#34D399]',
  earnings: 'bg-[#34D399]/15 text-[#34D399]',
  battle_invite: 'bg-[#FBBF24]/15 text-[#FBBF24]',
  battle_result: 'bg-[#FBBF24]/15 text-[#FBBF24]',
  went_live: 'bg-[#FF3B30]/15 text-[#FF3B30]',
  system: 'bg-white/10 text-zinc-400',
};

const CATEGORY_MAP = {
  all: null,
  mentions: 'mentions',
  activity: 'social',
  earnings: 'engagement',
};

export default function NotificationsPage() {
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'all');
  const [unreadCount, setUnreadCount] = useState(0);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) navigate('/login');
  }, [isAuthenticated, navigate]);

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      // Use smart grouped notifications
      const res = await fetch(`${API}/api/content-loop/smart-notifications?limit=50`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        let items = data.notifications || [];

        // Filter by tab
        if (activeTab === 'mentions') {
          items = items.filter(n => n.type === 'mention' || n.type === 'reply');
        } else if (activeTab === 'activity') {
          items = items.filter(n => ['follow', 'like_post', 'like_track', 'comment', 'repost'].includes(n.type));
        } else if (activeTab === 'earnings') {
          items = items.filter(n => ['tip_received', 'earnings', 'purchase', 'payout'].includes(n.type) || n.category === 'earnings');
        }

        if (showUnreadOnly) {
          items = items.filter(n => !n.read);
        }

        setNotifications(items);
        setUnreadCount(data.unread_count || 0);
      } else {
        // Fallback to standard notifications
        let url = `${API}/api/me/notifications?limit=100`;
        const category = CATEGORY_MAP[activeTab];
        if (category) url += `&category=${category}`;
        if (showUnreadOnly) url += '&unread_only=true';
        const fallbackRes = await fetch(url, { headers: getAuthHeaders() });
        if (fallbackRes.ok) {
          const data = await fallbackRes.json();
          setNotifications(data.notifications || []);
          setUnreadCount(data.unread_count || 0);
        }
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAuthHeaders, activeTab, showUnreadOnly]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API}/api/me/notifications/${notificationId}/read`, {
        method: 'PATCH', headers: getAuthHeaders(),
      });
      setNotifications(prev => prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Mark read failed:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API}/api/me/notifications/mark-all-read`, {
        method: 'PATCH', headers: getAuthHeaders(),
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All marked as read');
    } catch (err) {
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await fetch(`${API}/api/me/notifications/${notificationId}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      toast.success('Deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleClick = (n) => {
    if (!n.read) markAsRead(n.notification_id);
    if (n.link) navigate(n.link);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams(tab === 'all' ? {} : { tab });
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="notifications-page">
      {/* Header */}
      <div className="sticky top-0 z-20 backdrop-blur-xl bg-[#0A0A0A]/70 border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-zinc-400 hover:text-white hover:bg-white/5" data-testid="notif-back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  Notifications
                  {unreadCount > 0 && (
                    <Badge className="bg-[#FF3B30] text-white border-0 text-xs">{unreadCount}</Badge>
                  )}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs text-zinc-400 hover:text-white" data-testid="mark-all-read">
                  <CheckCheck className="w-3.5 h-3.5 mr-1" /> Mark all read
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={() => navigate('/settings/notifications')} className="text-zinc-400 hover:text-white" data-testid="notif-settings-btn">
                <Settings className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-3 overflow-x-auto">
            {Object.entries(TAB_CONFIG).map(([key, cfg]) => {
              const TabIcon = cfg.icon;
              return (
                <Button
                  key={key}
                  variant={activeTab === key ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handleTabChange(key)}
                  className={cn(
                    "text-xs whitespace-nowrap",
                    activeTab === key ? 'bg-[#2A2A2A] text-white' : 'text-zinc-400'
                  )}
                  data-testid={`notif-tab-${key}`}
                >
                  <TabIcon className="w-3.5 h-3.5 mr-1.5" />
                  {cfg.label}
                </Button>
              );
            })}
            <Button
              variant={showUnreadOnly ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setShowUnreadOnly(!showUnreadOnly)}
              className="text-xs ml-auto text-zinc-400"
              data-testid="unread-filter"
            >
              <Filter className="w-3.5 h-3.5 mr-1" />
              {showUnreadOnly ? 'Unread' : 'All'}
            </Button>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="max-w-3xl mx-auto px-4 py-4">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Bell className="w-14 h-14 text-zinc-700 mb-4" />
            <p className="text-zinc-400 font-medium">No notifications</p>
            <p className="text-xs text-zinc-600 mt-1 max-w-xs">
              {showUnreadOnly ? "You're all caught up!" : "When people interact with your content, you'll see it here."}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {notifications.map((n) => {
              const IconComp = ICON_MAP[n.type] || ICON_MAP[n.icon] || Bell;
              const colorClass = ICON_COLORS[n.type] || ICON_COLORS.system;
              return (
                <div
                  key={n.notification_id}
                  className={cn(
                    "flex gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 hover:bg-white/[0.03]",
                    !n.read && "bg-[#007AFF]/5 border border-[#007AFF]/10",
                    n.read && "border border-transparent"
                  )}
                  onClick={() => handleClick(n)}
                  data-testid={`notification-item-${n.notification_id}`}
                >
                  <div className={cn("w-10 h-10 rounded-full flex items-center justify-center shrink-0", colorClass)}>
                    <IconComp className="w-4.5 h-4.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={cn("text-sm leading-relaxed", !n.read && "font-medium")}>
                      {n.text || n.message || n.title || 'New notification'}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[11px] text-zinc-500">{timeAgo(n.created_at)}</span>
                      {n.grouped && n.count > 1 && (
                        <Badge className="bg-white/10 text-zinc-400 border-0 text-[10px] px-1.5">{n.count} events</Badge>
                      )}
                      {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#007AFF]" />}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!n.read && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-500 hover:text-white"
                        onClick={(e) => { e.stopPropagation(); markAsRead(n.notification_id); }}>
                        <Check className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-500 hover:text-[#FF3B30]"
                      onClick={(e) => { e.stopPropagation(); deleteNotification(n.notification_id); }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
